import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';

import { ToastContext, Card, Button, TextArea, StandardHeader } from '@weezevent/nacre';
import Styles from './ticketInfoDrawer.module.css';
import { default as SideBar } from './portal';

function TicketDataCard({ ticket = {}, onCopy = () => {}, formated_data = '' }) {
    const { t } = useTranslation();

    return (
        <Card className={Styles['card-data']}>
            <Card.Header>
                <Card.Title title={t('ticket.webservice.ticket-list.modal.title')} />
            </Card.Header>
            <Card.Content className={Styles['content-card']}>
                <TextArea disabled value={formated_data} style={{ resize: 'none' }} />
                <Button primary onClick={onCopy}>
                    {t('ticket.webservice.ticket-list.modal.copy')}
                </Button>
            </Card.Content>
        </Card>
    );
}

TicketDataCard.propTypes = {
    ticket: PropTypes.object.isRequired,
    onCopy: PropTypes.func.isRequired,
    formated_data: PropTypes.string.isRequired
};

function formatDate(date) {
    return new Date(date).toLocaleString('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short'
    });
}

function TicketHistoryCard({ ticket = {} }) {
    const { t } = useTranslation();

    const collectHistory = item => {
        // Original History
        const items = item.ticket_history.reduce((records, record, idx) => {
            if (record.pushed_at) {
                records.push(
                    <div key={idx} className={Styles['history-item']}>
                        {t('ticket.webservice.ticket-list.history.receivedAt', {
                            date: formatDate(record.pushed_at)
                        })}
                    </div>
                );
            } else if (record.pushed_to_weez_at) {
                records.push(
                    <div key={idx} className={Styles['history-item']}>
                        {t('ticket.webservice.ticket-list.history.pushedToWeez', {
                            date: formatDate(record.pushed_to_weez_at)
                        })}
                    </div>
                );
            }
            return records;
        }, []);

        // A little hack to add removed_at info at the end of history. We don't have another way to do that right now.
        if (ticket.removed_at) {
            items.push(
                <div key="removed" className={Styles['history-item']}>
                    {t('ticket.webservice.ticket-list.history.removedAt', {
                        date: formatDate(ticket.removed_at)
                    })}
                </div>
            );
        }

        return items;
    };

    const renderHistory = () => {
        if (!ticket.removed_at && ticket?.ticket_history?.length === 0) {
            return <p>{t('ticket.webservice.ticket-list.history.noHistory')}</p>;
        }
        return collectHistory(ticket);
    };

    return (
        <Card className={Styles['card-data']}>
            <Card.Header>
                <Card.Title title={t('ticket.common.history')} />
            </Card.Header>
            <Card.Content>{renderHistory()}</Card.Content>
        </Card>
    );
}

TicketHistoryCard.propTypes = {
    ticket: PropTypes.object.isRequired
};

function DrawerContent({ ticket = {}, formated_data = '', onCopy = () => {} }) {
    return (
        <div>
            <TicketDataCard ticket={ticket} onCopy={onCopy} formated_data={formated_data} />
            <TicketHistoryCard ticket={ticket} />
        </div>
    );
}

DrawerContent.propTypes = {
    ticket: PropTypes.object.isRequired,
    formated_data: PropTypes.string.isRequired,
    onCopy: PropTypes.func.isRequired
};

export function TicketInfoDrawer({ ticket = {}, openSideBar = false, onCloseSideBar = () => {} }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const formated_data = React.useMemo(() => JSON.stringify(ticket?.data, undefined, 4), [ticket]);

    const onCopy = React.useCallback(() => {
        navigator.clipboard.writeText(formated_data);
        toast.success(t('ticket.webservice.ticket-list.modal.toast-success'));
    }, [formated_data, toast]);

    if (!ticket) {
        return null;
    }

    return (
        <SideBar className={Styles['drawer']} open={openSideBar} onClickAway={onCloseSideBar} onClose={onCloseSideBar} size="medium">
            <StandardHeader title={ticket.ticket_identifier} />
            <DrawerContent ticket={ticket} onCopy={onCopy} formated_data={formated_data} />
        </SideBar>
    );
}

TicketInfoDrawer.propTypes = {
    ticket: PropTypes.object,
    openSideBar: PropTypes.bool,
    onCloseSideBar: PropTypes.func
};
